import {IDevice, IDeviceAPI, IDeviceBatchDeleteResponse, IDeviceBatchGetResponse, IDeviceCreateResponse, IDeviceForm, IDeviceQuery, IDeviceUriInput} from 'interfaces';
import {axiosAPI} from './API';
import { LocationService } from './LocationService';
import { ManufacturerService } from './ManufacturerService';
import { ModalState } from 'enums';

// device crud operations, conversions, and caching
class _DeviceService {

    public allDevices: IDevice[] = [];

    public getDevices = (query: IDeviceQuery): Promise<IDevice[]> => {

        const p = axiosAPI.get<IDeviceAPI[]>('/devices', {params: query})
            .then((resp) => {
                var devices = resp.data.map(this.mapDeviceFromServer);
                this.allDevices = devices;
                return devices;
            });
            
        p.catch((err) => {
            // handle 404 composite query
            if (err.response.status == 404) return;
            console.error('Error getting devices: ', err);
        });

        return p;
    }

    public createDevice = (device: IDevice): Promise<IDeviceCreateResponse> => {

        const p = axiosAPI.post<IDeviceCreateResponse>('/devices', this.mapDeviceToServer(device))
            .then((resp) => {
                return resp.data;
            });
            
        p.catch((err) => {
            console.error('Error creating device: ', err);
        });

        return p;
    }

    public updateDevice = (id: number, device: IDevice): Promise<IDeviceCreateResponse> => {

        const p = axiosAPI.patch<IDeviceCreateResponse>(`/devices/${id}`, this.mapDeviceToServer(device))
            .then((resp) => {
                const i = this.allDevices.findIndex(d => d.id === id);
                this.allDevices.splice(i, 1, device);
                return resp.data;
            });
            
        p.catch((err) => {
            console.error('Error updating device: ', err);
        });

        return p;
    }

    public deleteDevice = (id: number): Promise<any> => {

        const p = axiosAPI.delete(`/devices/${id}`)
            .then((resp) => {
                this.allDevices = this.allDevices.filter(d => d.id != id);
                return resp.data;
            });
            
        p.catch((err) => {
            console.error('Error deleting device: ', err);
        });

        return p;
    }

    public batchGetDevices = (thingIDs: string[]): Promise<IDevice[]> => {

        let queries: IDeviceQuery[] = [];
        thingIDs.forEach((t) => queries.push({thingID: t}));

        const p = axiosAPI.post<IDeviceBatchGetResponse[]>('/devices/batchget', queries)
            .then((resp) => {
                var devices = resp.data[0].result.map(this.mapDeviceFromServer);
                this.allDevices = devices;
                return devices;
            });
            
        p.catch((err) => {
            console.error('Error batch getting devices: ', err);
        });

        return p;
    }

    public batchDeleteDevices = (devs: IDevice[]): Promise<IDevice[]> => {

        let queries: IDeviceUriInput[] = [];
        let ids: number[] = [];

        devs.forEach((t) => {
            queries.push({id: t.id});
            ids.push(t.id);
        });

        const p = axiosAPI.delete<IDeviceBatchDeleteResponse[]>('/devices/batch', {data: queries})
            .then(() => {
                const idsSet = new Set(ids);
                let newDevs = this.allDevices.filter((item) => !idsSet.has(item.id));
                this.allDevices = newDevs;
                return newDevs;
            });
            
        p.catch((err) => {
            console.error('Error batch deleting devices: ', err);
        });

        return p;
    }

    public mapDeviceFromServer = (d: IDeviceAPI): IDevice => {
        return {
            ...d,
            location: LocationService.mapLocationFromServer(d.location),
            manufacturer: ManufacturerService.mapManufacturerFromServer(d.manufacturer),
            createdAt: d.createdAt ? new Date(d.createdAt) : undefined,
            updatedAt: d.updatedAt ? new Date(d.updatedAt) : undefined,
            lastSeen: d.lastSeen ? new Date(d.lastSeen) : undefined,
            deviceLastSeen: d.deviceLastSeen ? new Date(d.deviceLastSeen) : undefined,
        }
    }

    public mapDeviceToServer = (d: IDevice): IDeviceAPI => {
        return {
            ...d,
            id: d.id ? d.id : 0,
            location: LocationService.mapLocationToServer(d.location),
            manufacturer: ManufacturerService.mapManufacturerToServer(d.manufacturer),
            createdAt: d.createdAt ? d.createdAt.toISOString() : "",
            updatedAt: d.updatedAt ? d.updatedAt.toISOString() : "",
            lastSeen: d.lastSeen ? d.lastSeen.toISOString() : "",
            deviceLastSeen: d.deviceLastSeen ? d.deviceLastSeen.toISOString() : "",
        }
    }

    public mapDeviceToForm = (d: IDevice, state: ModalState): IDeviceForm => {
        // convert empty objects to lists
        let firmware = [];
        let menu = [];
        let updateStatus = [];

        if (d.firmware) firmware = Object.entries(d.firmware).map(([key, value]) => ({ key, value }));
        if (d.menu) menu = Object.entries(d.menu).map(([key, value]) => ({ key, value }));
        if (d.updateStatus) updateStatus = Object.entries(d.updateStatus).map(([key, value]) => {
            return { key, value: value.status + " - " + value.version}
        });

        return {
            ...d,
            firmware: firmware,
            menu: menu,
            updateStatus: updateStatus,
        };
    }

    public mapDeviceFromForm = (d: IDeviceForm): IDevice => {
        // convert empty lists to objects
        let firmware = {};
        let menu = {};
        let updateStatus = {};

        if (d.firmware?.length > 0) firmware = Object.fromEntries(d.firmware.map(({ key, value }) => [key, value]));
        if (d.menu?.length > 0) menu = Object.fromEntries(d.menu.map(({ key, value }) => [key, value]));
        if (d.updateStatus?.length > 0) { updateStatus = Object.fromEntries(d.updateStatus.map(({ key, value }) => {
            let values = value.split(" - ");
            return [key, {status: values[0], version: values[1]}];
        }))}

        return {
            ...d,
            firmware: firmware,
            menu: menu,
            updateStatus: updateStatus,
        }
    }
}

export const DeviceService = new _DeviceService();
